import React, { useState, useEffect } from "react";
import { customFetch } from '../apiClient';

const DaybookReport = () => {
  // ✅ Set default to current date
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  
  const [allRows, setAllRows] = useState([]);
  const [displayRows, setDisplayRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15); // ✅ Changed default to 15 for compact view
  const [totalRecords, setTotalRecords] = useState(0);
  const token = sessionStorage.getItem("token");

  // Auto-dismiss alerts
  useEffect(() => {
    if (success) { const timer = setTimeout(() => setSuccess(""), 3000); return () => clearTimeout(timer); }
  }, [success]);

  useEffect(() => {
    if (error) { const timer = setTimeout(() => setError(""), 4000); return () => clearTimeout(timer); }
  }, [error]);

  const applyPagination = (data, page, rows) => {
    const startIndex = (page - 1) * rows;
    const endIndex = startIndex + rows;
    setDisplayRows(data.slice(startIndex, endIndex));
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    let value = parseInt(e.target.value);
    if (isNaN(value) || value < 1) value = 15;
    if (value > 100) value = 100;
    setRowsPerPage(value); setCurrentPage(1); applyPagination(allRows, 1, value);
  };

  const handlePageChange = (page) => { applyPagination(allRows, page, rowsPerPage || 15); };

  const effectiveRows = rowsPerPage || 15;
  const totalPages = Math.ceil(totalRecords / effectiveRows);

  const handleSearch = async () => {
    if (!fromDate || !toDate) { setError("Please select From and To date"); return; }
    try {
      setLoading(true); setError(""); setSuccess("");
      const params = new URLSearchParams();
      params.append("fromDate", new Date(fromDate).toISOString());
      params.append("toDate", new Date(toDate).toISOString());

     const res = await customFetch(`/AccountMaster/daybook?${params.toString()}`);

      if (!res.ok) throw new Error("Failed to fetch daybook data");
      const data = await res.json();

      setAllRows(data); setTotalRecords(data.length);
      applyPagination(data, 1, rowsPerPage || 15);
      if (data.length > 0) setSuccess(`Loaded ${data.length} records`);
    } catch (err) {
      console.error("Daybook fetch failed", err);
      setError(err.message || "Failed to load daybook data");
      setAllRows([]); setDisplayRows([]); setTotalRecords(0);
    } finally { setLoading(false); }
  };

  const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
  };

  const formatNumber = (num) => (typeof num === "number" ? num.toFixed(2) : num ?? "");

  // Calculate Totals
  const totalCredit = allRows.reduce((sum, row) => sum + (Number(row.CREDIT) || 0), 0);
  const totalDebit = allRows.reduce((sum, row) => sum + (Number(row.DEBIT) || 0), 0);
  const NetBalance = totalCredit - totalDebit;

  const pageTotalCredit = displayRows.reduce((sum, row) => sum + (Number(row.CREDIT) || 0), 0);
  const pageTotalDebit = displayRows.reduce((sum, row) => sum + (Number(row.DEBIT) || 0), 0);

  const renderPagination = () => {
    if (!effectiveRows || totalRecords === 0) return null;
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

    return (
      <div className="dc-pagination">
        <button className="dc-btn-page" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}><i className="bi bi-chevron-left"></i></button>
        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(num => (
            <button key={num} className={`dc-btn-page ${currentPage === num ? "active" : ""}`} onClick={() => handlePageChange(num)}>{num}</button>
        ))}
        <button className="dc-btn-page" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}><i className="bi bi-chevron-right"></i></button>
      </div>
    );
  };

  return (
    <div className="dc-wrap">
      <div className="dc-card">
        
        {/* Compact Header */}
        <div className="dc-header">
          <div className="dc-header-inner">
            <div className="dc-header-brand">
              <div className="dc-header-icon"><i className="bi bi-journal-text"></i></div>
              <span className="dc-header-title">Daybook Report</span>
            </div>
          </div>
        </div>

        <div className="dc-body">
          {error && <div className="dc-alert dc-alert-danger"><i className="bi bi-exclamation-triangle-fill"></i> {error}</div>}
          {success && <div className="dc-alert dc-alert-success"><i className="bi bi-check-circle-fill"></i> {success}</div>}

          {/* Compact Filters */}
          <div className="dc-section">
            <div className="dc-section-title"><i className="bi bi-funnel"></i><span>Filters</span></div>
            <div className="dc-section-body">
              <div className="dc-grid dc-grid-auto" style={{ alignItems: 'end' }}>
                <div>
                  <label className="dc-label">From Date</label>
                  <input type="date" className="dc-input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={toDate} />
                </div>
                <div>
                  <label className="dc-label">To Date</label>
                  <input type="date" className="dc-input" value={toDate} onChange={(e) => setToDate(e.target.value)} min={fromDate} />
                </div>
                <div style={{ maxWidth: '80px' }}>
                  <label className="dc-label">Rows</label>
                  <input type="number" className="dc-input" value={rowsPerPage} min="1" max="100" onChange={handleRowsPerPageChange} />
                </div>
                <div>
                  <button className="dc-btn-submit" onClick={handleSearch} disabled={loading}>
                    <i className="bi bi-search"></i> {loading ? "..." : "Search"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Compact Totals Cards */}
          {totalRecords > 0 && (
            <div className="dc-grid dc-grid-4 dc-mb2">
              <div className="dc-total-card dc-credit">
                <div className="dc-total-icon"><i className="bi bi-arrow-down-circle"></i></div>
                <div className="dc-total-info"><span className="dc-total-lbl">Total Credit</span><span className="dc-total-val">₹{totalCredit.toFixed(2)}</span></div>
              </div>
              <div className="dc-total-card dc-debit">
                <div className="dc-total-icon"><i className="bi bi-arrow-up-circle"></i></div>
                <div className="dc-total-info"><span className="dc-total-lbl">Total Debit</span><span className="dc-total-val">₹{totalDebit.toFixed(2)}</span></div>
              </div>
              <div className="dc-total-card dc-balance">
                <div className="dc-total-icon"><i className="bi bi-cash-stack"></i></div>
                <div className="dc-total-info"><span className="dc-total-lbl">Net Balance</span><span className={`dc-total-val ${NetBalance >= 0 ? 'dc-text-success' : 'dc-text-danger'}`}>₹{NetBalance.toFixed(2)}</span></div>
              </div>
              <div className="dc-total-card dc-info">
                <div className="dc-total-icon"><i className="bi bi-list-ol"></i></div>
                <div className="dc-total-info"><span className="dc-total-lbl">Records</span><span className="dc-total-val">{totalRecords}</span></div>
              </div>
            </div>
          )}

          {/* Compact Table */}
          <div className="dc-table-wrap">
            <table className="dc-table">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>ID</th>
                  <th style={{ width: '100px' }}>Date</th>
                  <th>Description</th>
                  <th className="dc-text-right" style={{ width: '120px' }}>Credit (₹)</th>
                  <th className="dc-text-right" style={{ width: '120px' }}>Debit (₹)</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.length === 0 ? (
                  <tr><td colSpan="5" className="dc-empty">{loading ? "Loading..." : "No records found."}</td></tr>
                ) : (
                  displayRows.map((row, idx) => (
                    <tr key={idx}>
                      <td className="dc-text-muted">{String(row.ID)}</td>
                      <td>{formatDate(row.DDATE)}</td>
                      <td>{String(row.DESCRIPTIONS)}</td>
                      <td className="dc-text-right dc-text-success dc-fw-bold">{formatNumber(row.CREDIT)}</td>
                      <td className="dc-text-right dc-text-danger dc-fw-bold">{formatNumber(row.DEBIT)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {displayRows.length > 0 && (
                <tfoot>
                  <tr className="dc-table-footer">
                    <th colSpan="3" className="dc-text-right">Page Total</th>
                    <th className="dc-text-right dc-text-success">₹{pageTotalCredit.toFixed(2)}</th>
                    <th className="dc-text-right dc-text-danger">₹{pageTotalDebit.toFixed(2)}</th>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {totalPages > 1 && renderPagination()}

        </div>
      </div>

      {/* ✅ PURE COMPACT CSS (ZERO BOOTSTRAP OVERRIDES) */}
      <style jsx>{`
        *, *::before, *::after { box-sizing: border-box; }
        .dc-wrap { padding: 0; font-family: system-ui, sans-serif; }
        
        .dc-card { background: #fff; border-radius: 6px; box-shadow: 0 1px 8px rgba(0,0,0,0.06); border: 1px solid #e0e6ed; overflow: hidden; }
        
        .dc-header { background: var(--theme-color, #0088cc); padding: 6px 12px; border-bottom: 2px solid rgba(0,0,0,0.1); }
        .dc-header-inner { display: flex; align-items: center; }
        .dc-header-brand { display: flex; align-items: center; gap: 6px; color: white; font-weight: 600; font-size: 13px; }
        .dc-header-icon { background: rgba(255,255,255,0.2); width: 24px; height: 24px; border-radius: 4px; display: flex; align-items: center; justify-content: center; }
        
        .dc-body { padding: 10px 12px; }

        .dc-section { background: #f8f9fa; border: 1px solid #e0e6ed; border-radius: 5px; margin-bottom: 10px; }
        .dc-section-title { background: var(--theme-color, #0088cc); padding: 5px 10px; display: flex; align-items: center; gap: 5px; border-radius: 4px 4px 0 0; color: white; font-size: 11px; font-weight: 600; }
        .dc-section-body { padding: 8px 10px; background: #fff; border-radius: 0 0 5px 5px; }

        .dc-grid { display: grid; gap: 8px; align-items: start; }
        .dc-grid-auto { grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); }
        .dc-grid-4 { grid-template-columns: repeat(4, 1fr); }
        .dc-mb2 { margin-bottom: 10px; }
        
        .dc-label { display: block; font-size: 10px; font-weight: 600; color: #2c3e50; margin-bottom: 2px; }
        .dc-input { width: 100%; height: 26px; min-height: 26px; padding: 2px 6px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; color: #2c3e50; outline: none; }
        .dc-input:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0,136,204,0.15); }

        .dc-btn-submit { height: 26px; padding: 0 14px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px; }
        .dc-btn-submit:hover:not(:disabled) { opacity: 0.9; }
        .dc-btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Compact Totals Cards */
        .dc-total-card { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 5px; border: 1px solid #e0e6ed; background: #fff; }
        .dc-credit { border-left: 3px solid #28a745; background: #f0fdf4; }
        .dc-debit { border-left: 3px solid #dc3545; background: #fef2f2; }
        .dc-balance { border-left: 3px solid #0088cc; background: #eff6ff; }
        .dc-info { border-left: 3px solid #6c757d; background: #f8f9fa; }
        
        .dc-total-icon { font-size: 16px; opacity: 0.8; }
        .dc-credit .dc-total-icon { color: #28a745; }
        .dc-debit .dc-total-icon { color: #dc3545; }
        .dc-balance .dc-total-icon { color: #0088cc; }
        .dc-info .dc-total-icon { color: #6c757d; }

        .dc-total-info { display: flex; flex-direction: column; }
        .dc-total-lbl { font-size: 9px; font-weight: 600; color: #6c757d; text-transform: uppercase; }
        .dc-total-val { font-size: 13px; font-weight: 700; color: #2c3e50; }

        /* Table */
        .dc-table-wrap { overflow-x: auto; border: 1px solid #e0e6ed; border-radius: 4px; }
        .dc-table { width: 100%; border-collapse: collapse; font-size: 11px; min-width: 600px; }
        .dc-table th { background: var(--theme-color, #0088cc); color: white; padding: 6px 8px; font-weight: 600; text-align: left; }
        .dc-table td { padding: 5px 8px; border-bottom: 1px solid #e0e6ed; vertical-align: middle; }
        .dc-table tr:hover { background: #f0f7ff; }
        .dc-table-footer th { background: #e7f3ff; color: #2c3e50; padding: 6px 8px; border-top: 2px solid #cbd5e0; }
        
        .dc-text-right { text-align: right !important; }
        .dc-text-center { text-align: center !important; }
        .dc-text-muted { color: #6c757d; }
        .dc-text-success { color: #28a745 !important; }
        .dc-text-danger { color: #dc3545 !important; }
        .dc-fw-bold { font-weight: 700; }
        .dc-empty { text-align: center; padding: 20px; color: #7f8c8d; }

        /* Pagination */
        .dc-pagination { display: flex; justify-content: center; gap: 4px; margin-top: 10px; }
        .dc-btn-page { width: 24px; height: 24px; border: 1px solid #cbd5e0; background: white; border-radius: 4px; cursor: pointer; font-size: 11px; display: flex; align-items: center; justify-content: center; }
        .dc-btn-page:hover:not(:disabled) { background: #f1f3f5; }
        .dc-btn-page.active { background: var(--theme-color, #0088cc); color: white; border-color: var(--theme-color, #0088cc); }
        .dc-btn-page:disabled { opacity: 0.5; cursor: not-allowed; }

        .dc-alert { padding: 6px 10px; border-radius: 4px; font-size: 11px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .dc-alert-danger { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
        .dc-alert-success { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }

        @media (max-width: 768px) {
          .dc-grid-4 { grid-template-columns: repeat(2, 1fr); }
          .dc-btn-submit { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default DaybookReport;